import { IconArrowForward } from "@tine/designsystem-icons/sharp";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { useInView } from "react-intersection-observer";
import { Link } from "wouter";

import useContainerWidth, { type WidthRanges } from "../../common/hooks/useContainerWidth";
import { logger } from "../../common/logger";
import { sendSelectTeaser, sendViewTeaser } from "../../common/tracking";
import type { AspectRatioType } from "../../common/types/mediaTypes";
import ImageWithFocus from "./ImageWithFocus";

type UrlImageLinkProps = {
   url: string;
   imageWithFocusId?: string;
   title?: string;
   subTitle?: string;
   trackingListName?: string;
   type?: string;
};

type BreakpointSize = "small" | "large";

const ranges: WidthRanges<BreakpointSize> = {
   small: [0, 700],
   large: [700]
};

const UrlImageLink = ({ url, imageWithFocusId, title, subTitle, trackingListName, type = "url" }: UrlImageLinkProps) => {
   const trackImpression = (isVisible: boolean) => {
      if (isVisible) {
         sendViewTeaser(title, type, trackingListName);
      }
   };

   const { ref: viewRef } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   const { ref: containerRef, size: currentContainerSize } = useContainerWidth<BreakpointSize, HTMLDivElement>(ranges);

   const largeContainer = currentContainerSize === "large";
   const width = largeContainer ? 1200 : 800;
   const ratio: AspectRatioType = largeContainer ? "32x9" : "16x9";
   const hasSubTitle = !!subTitle;
   const hasTitle = !!title;
   const hasText = hasTitle || hasSubTitle;
   const hasOnlySubTitle = hasSubTitle && !hasTitle;

   const subTitleStyle = "p-0 m-0 text-[0.67em] overflow-hidden text-ellipsis whitespace-nowrap hidden @[500px]:inline rounded";

   const gradientStyle = cn(
      "bg-gradient-to-b from-[#0000] from-55% to-[#232323ab] to-100% shadow-[#00000080] text-shadow hover:from-[#00000024] hover:to-[#232323ab]",
      {
         "from-65%": largeContainer
      }
   );

   const titlesTextStyle = "text-base text-balance @[360px]:text-xl @[500px]:text-2xl @[700px]:text-3xl";
   const gradientOverlayStyle = cn("absolute inset-0 hidden rounded", titlesTextStyle, gradientStyle, {
      block: hasTitle,
      "@[500px]:block": hasOnlySubTitle
   });

   const titlesContainerStyle =
      "absolute bottom-0 rounded p-4 text-base-0 @[360px]:pb-5 @[500px]:pb-6 @[700px]:w-full @[700px]:px-16 @[700px]:pb-10 @[700px]:text-center";
   const titleStyle = cn(titlesTextStyle, "m-0 p-0");

   const ariaLabel = `Gå til ${title ?? url}`;

   if (!imageWithFocusId) {
      logger.warn("contentful", `No image specified for UrlImageLink to ${url}`);
      return null;
   }

   function handleClickImageLink() {
      sendSelectTeaser(title, type, trackingListName, url);
   }

   return (
      <div ref={viewRef}>
         <div ref={containerRef} className={"relative @container"}>
            <Link aria-label={ariaLabel} to={url} onClick={() => handleClickImageLink()}>
               <ImageWithFocus
                  width={width}
                  entryId={imageWithFocusId}
                  placement={"belowFold"}
                  ratio={ratio}
                  className="rounded-1"
               />

               {hasText && (
                  <div className={gradientOverlayStyle}>
                     <div className={titlesContainerStyle}>
                        <h2 className={titleStyle}>{title}</h2>
                        {!!subTitle && (
                           <p className={subTitleStyle}>
                              {subTitle}
                              <IconArrowForward className="inline" />
                           </p>
                        )}
                     </div>
                  </div>
               )}
            </Link>
         </div>
      </div>
   );
};

export default observer(UrlImageLink);
